// #1872 — in-flight request coalescing + a client-side concurrency cap.
//
// ══ WHY THIS EXISTS ══
//
// Measured on stage- with per-request nginx timing: ONE assign-page load fires
// 57 requests, 21 of them inside a single second, at a box with 2 cores. The
// decisive test on that box, same 85-byte authenticated endpoint:
//
//     one at a time      ~0.40 s
//     20 at once          median 2.87 s   max 4.34 s
//
// The identical request is 7-10x slower under the page's own concurrency. That
// is the entire gap between the controller (354 ms profiled directly) and the
// 4.2 s the browser sees. The work is not slow; the page asks for all of it at
// once and the box thrashes.
//
// ══ WHY AT THIS SEAM ══
//
// `getEmployeesBySkill` is fetched 3x per load, one an EXACT duplicate — same
// call id, same URL, 407 KB each. The triggers are spread across four assign
// components and several bus listeners, and removing the wrong one silently
// drops a legitimate refresh (an assign that no longer updates the grid). Every
// component already goes through `services.js`, so fixing it here makes a
// duplicate impossible BY CONSTRUCTION however it was triggered, and covers the
// other repeats in the same stroke (employee_status_lite x5, checkSkill x3).
//
// ══ COALESCING IS NOT CACHING ══
//
// Only requests IN FLIGHT AT THE SAME MOMENT share a promise. Once one settles
// its entry is dropped, so the next call issues a real request. "Assign, then
// refetch to refresh the grid" still refetches — the refetch happens after the
// action, not during the original fetch. Nothing is ever served stale, which is
// why this needs no TTL and no invalidation, unlike the #1442 client cache.
//
// A rejection is delivered to every waiter and ALSO clears the entry, so a
// failure never wedges an endpoint for the life of the page.

export function createCoalescer({ maxConcurrent = 6 } = {}) {
    const inFlight = new Map();   // key -> promise shared by all waiters
    const queue = [];             // [{ start }] FIFO, so queued work keeps its order
    let active = 0;

    function pump() {
        while (active < maxConcurrent && queue.length) {
            const next = queue.shift();
            active++;
            next.start();
        }
    }

    function request(key, run) {
        const existing = inFlight.get(key);
        if (existing) {
            return existing;
        }

        const promise = new Promise((resolve, reject) => {
            queue.push({
                start() {
                    let p;
                    // A `run` that throws SYNCHRONOUSLY must still release its
                    // slot, or one bad call site stalls the queue permanently.
                    try {
                        p = Promise.resolve(run());
                    } catch (err) {
                        p = Promise.reject(err);
                    }
                    // ⚠ RELEASE BEFORE RESOLVING, not in a chained .then().
                    //
                    // Chaining cleanup after resolve() defers it by a microtask,
                    // and a caller resuming from `await` runs FIRST — so it finds
                    // the settled entry still in the map and gets the previous
                    // response back. That turns coalescing into an unbounded
                    // cache with no TTL: "assign, then refetch" would re-serve
                    // the pre-assign grid. Caught by the "coalescing != caching"
                    // test, which is the one that must never be deleted.
                    p.then(
                        (value) => { release(); resolve(value); },
                        (err)   => { release(); reject(err); }
                    );
                },
            });
        });

        function release() {
            active--;
            inFlight.delete(key);
            pump();
        }

        inFlight.set(key, promise);
        pump();
        return promise;
    }

    return { request, get _active() { return active; }, get _queued() { return queue.length; } };
}
